// ── Unthrottled Interval Timer ────────────────────────────────────
//
// Browsers aggressively throttle (or freeze) main-thread setInterval /
// setTimeout in tabs that are backgrounded, hidden, or on a sleeping device.
// For the live-stream heartbeat this is fatal: if the streamer tabs over to
// OBS, the beat stops and viewers see them drop offline within the 4-minute
// tolerance even though the video feed is fine.
//
// A Web Worker's timers run on their own thread and are NOT throttled the
// same way, so a worker-driven tick keeps firing while the tab is hidden.
// (It still cannot survive the user fully closing the tab or the OS hard-
// sleeping the whole machine — nothing browser-side can.)
//
// The worker is built from an inline Blob (no separate bundled file). The
// app's CSP already allows this via `worker-src 'self' blob:`.
//
// If Worker/Blob aren't available for any reason, we fall back to a normal
// setInterval so behaviour degrades gracefully rather than the heartbeat
// silently never starting.

export interface UnthrottledInterval {
  /** Stop the timer and release the worker. Safe to call multiple times. */
  stop: () => void;
}

const WORKER_SOURCE = `
  let timer = null;
  self.onmessage = (e) => {
    const data = e.data || {};
    if (data.cmd === 'start') {
      if (timer !== null) { clearInterval(timer); }
      const ms = typeof data.ms === 'number' && data.ms > 0 ? data.ms : 1000;
      timer = setInterval(() => { self.postMessage('tick'); }, ms);
    } else if (data.cmd === 'stop') {
      if (timer !== null) { clearInterval(timer); timer = null; }
    }
  };
`;

/**
 * Run `onTick` every `intervalMs`, using a Web Worker so the timer keeps
 * firing even when the tab is backgrounded. Falls back to setInterval when
 * workers aren't supported.
 *
 * Note: the interval is fixed. Callers that want dynamic backoff should stop
 * and restart with a new interval (see the heartbeat loop).
 */
export function startUnthrottledInterval(intervalMs: number, onTick: () => void): UnthrottledInterval {
  // ── Preferred path: Web Worker via Blob URL ──
  if (typeof Worker !== 'undefined' && typeof Blob !== 'undefined' && typeof URL !== 'undefined' && URL.createObjectURL) {
    try {
      const blob = new Blob([WORKER_SOURCE], { type: 'application/javascript' });
      const url = URL.createObjectURL(blob);
      const worker = new Worker(url);
      let stopped = false;

      worker.onmessage = () => {
        if (!stopped) onTick();
      };

      worker.postMessage({ cmd: 'start', ms: intervalMs });

      return {
        stop: () => {
          if (stopped) return;
          stopped = true;
          try { worker.postMessage({ cmd: 'stop' }); } catch { /* ignore */ }
          try { worker.terminate(); } catch { /* ignore */ }
          try { URL.revokeObjectURL(url); } catch { /* ignore */ }
        },
      };
    } catch {
      // fall through to setInterval fallback below
    }
  }

  // ── Fallback: main-thread setInterval (may be throttled in bg tabs) ──
  const id = setInterval(onTick, intervalMs);
  return {
    stop: () => { clearInterval(id); },
  };
}
